import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Card, Tag, Space, Modal, Form, Select, Switch, Tooltip } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  loadKBArticles,
  createKBArticle,
  updateKBArticle,
  deleteKBArticle
} from 'store/slices/SupportSlice';
import useUrlFilters from 'hooks/useUrlFilters';

const { Search } = Input;
const { TextArea } = Input;
const { confirm } = Modal;

const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const SupportKnowledgeBase = () => {
  const dispatch = useDispatch();

  const articles = useSelector(state => state.support.kbArticles);
  const loading = useSelector(state => state.support.kbLoading);
  const pagination = useSelector(state => state.support.kbPagination);
  const ticketCategories = useSelector(state => state.support.ticketCategories);

  // Search and page live in the URL.
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const searchValue = filters.search || '';
  const [searchLive, setSearchLive] = useState(searchValue);
  useEffect(() => setSearchLive(searchValue), [searchValue]);
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData(currentPage);
  }, [searchValue, currentPage, pageSize]);

  const loadData = (page) => {
    dispatch(loadKBArticles({ page, searchValue, page_size: pageSize }));
  };

  const handleSearch = (value) => {
    setFilters({ search: value, page: 1 });
  };

  const handleCreate = () => {
    setEditingArticle(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingArticle(record);
    form.setFieldsValue({
      title: record.title,
      content: record.content,
      category: record.category,
      is_published: record.is_published,
      is_featured: record.is_featured
    });
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Delete Article',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.title}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        dispatch(deleteKBArticle(record.id));
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingArticle) {
        await dispatch(updateKBArticle({ id: editingArticle.id, ...values })).unwrap();
      } else {
        await dispatch(createKBArticle(values)).unwrap();
      }
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Failed to save article:', error);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category',
      width: 150
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.is_published ? 'green' : 'default'}>
          {record.is_published ? 'Published' : 'Draft'}
        </Tag>
      )
    },
    {
      title: 'Featured',
      dataIndex: 'is_featured',
      key: 'featured',
      width: 100,
      render: (featured) => featured ? <Tag color="gold">⭐ Featured</Tag> : '-'
    },
    {
      title: 'Views',
      dataIndex: 'view_count',
      key: 'views',
      width: 80,
      align: 'center'
    },
    {
      title: 'Helpful',
      dataIndex: 'helpful_count',
      key: 'helpful',
      width: 80,
      align: 'center'
    },
    {
      title: 'Helpfulness',
      dataIndex: 'helpfulness_score',
      key: 'score',
      width: 110,
      render: (score) => score > 0 ? `${score}%` : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            href={`${process.env.REACT_APP_SUPPORT_PORTAL_URL || 'http://localhost:3001'}/knowledge-base/${record.slug}`}
            target="_blank"
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Search
            placeholder="Search articles..."
            value={searchLive}
            onChange={(e) => setSearchLive(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
          />

          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadData(currentPage)}
              aria-label="Refresh"
            />
          </Tooltip>
        </Space>
      </Card>

      <Card
        title="Knowledge Base Articles"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Article
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={articles}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            total: pagination.totalCount,
            pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} articles`,
            // A new page size starts again from page 1
            onChange: (page, size) => setFilters({ page: size !== pageSize ? 1 : page, pageSize: size })
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingArticle ? 'Edit Article' : 'Create Article'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_published: false,
            is_featured: false
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter article title' }]}
          >
            <Input placeholder="Article title" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: 'Please enter article content' }]}
          >
            <TextArea
              rows={10}
              placeholder="Article content (supports Markdown)"
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {ticketCategories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.icon && <span style={{ marginRight: 8 }}>{cat.icon}</span>}
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_published"
            label="Published"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="is_featured"
            label="Featured"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportKnowledgeBase;
